import EditConflictInspector from './EditConflictInspector';
import useRecoverableDraft from '../hooks/useRecoverableDraft';
import RecoverableDraftNotice from './RecoverableDraftNotice';
import { newSaveRequestKey } from '../utils/saveSafety';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  MenuItem,
  Box,
  Typography,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  InputAdornment,
  CircularProgress,
  IconButton,
  Alert,
  FormControlLabel,
  Switch
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Edit as EditIcon,
  ReceiptLong as InvoiceIcon,
  ShoppingCart as POIcon,
  Description as QuoteIcon,
  FolderOpen as ProjectIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  CurrencyExchange as CurrencyIcon,
  DeleteOutline as DeleteIcon,
  AddCircleOutline as AddIcon,
  ImageOutlined as ImageIcon,
  Autorenew as ReplaceIcon,
  LocalOfferOutlined as DiscountIcon
} from '@mui/icons-material';

import invoiceService from '../services/invoiceService';
import projectService from '../services/projectService';
const INITIAL_STATUS_CONFIG = invoiceService.getFallbackStatusRules();

const PO_META_TAG = '__PO_META__';
const FIXED_CUSTOMER_ADDRESS = 'AV. Industrial 170';
const AUTO_VALIDITY_DAYS = 25;
const DEFAULT_PO_TERMS = [
  'Currency: All prices are expressed in the selected currency.',
  'Payment terms: Payment must be made within the agreed terms specified in this quotation.',
  'Lead time: Estimated delivery time starts from order confirmation.',
  'Order confirmation: The purchase order must be formally sent to the designated email address.',
  'Cancellations: Once the order is confirmed and payment received, no cancellations are accepted.'
].join('\n');
const MARKDOWN_FORMAT_SNIPPETS = [
  { label: 'Bold', snippet: '**Important text**' },
  { label: 'Italic', snippet: '*Italic text*' },
  { label: 'Title', snippet: '# Section title' },
  { label: 'List', snippet: '- Point 1\n- Point 2' },
  { label: 'Numbering', snippet: '1. First step\n2. Second step' },
  { label: 'Quote', snippet: '> Relevant note for the client' },
  { label: 'Link', snippet: '[SMTLINKSS Portal](https://smtlinkss.com)' },
  { label: 'Underline', snippet: '__Underlined text__' },
  { label: 'Strikethrough', snippet: '~~Replaced text~~' },
  { label: 'Code', snippet: '`ITEM-001`' }
];
const MARKDOWN_ADVANCED_EXAMPLE = [
  '# Project scope',
  '**Phase 1:** System integration',
  '- Discovery',
  '- Implementation',
  '',
  '1. Technical delivery',
  '2. Training',
  '',
  '> Scope may change after client validation',
  '',
  'More info: [SMTLINKSS Portal](https://smtlinkss.com)'
].join('\n');
const ITEM_DETAIL_TEMPLATE = [
  'Automatic [equipment or service] with its main functions and benefits.',
  '',
  '*The technical data may change or vary due to the selected options.*'
].join('\n');
const ITEM_TECHNICAL_SPECS_TEMPLATE = [
  'Format (X / Y): ',
  'Material: ',
  'Capacity: ',
  'Operating range: ',
  'Power supply: ',
  'Dimensions: ',
  'Weight: ',
  'Lead time: '
].join('\n');
const ISSUER_ADDRESS_PROFILES = [
  { value: 'mx', label: 'SMTLINK SOLUTIONS, S.A. de C.V. — México' },
  { value: 'us', label: 'SMTLINK SOLUTIONS LLC — USA' }
];

const isQuoteOrPO = (type) => type === 'quote' || type === 'purchase_order';
const FALLBACK_STATUS_OPTIONS_BY_TYPE = {
  invoice: ['pending', 'paid', 'overdue', 'cancelled'],
  quote: ['draft', 'sent', 'approved', 'rejected', 'expired', 'pending'],
  purchase_order: ['pending', 'approved', 'in_progress', 'completed', 'cancelled', 'rejected', 'paid']
};
const FALLBACK_STATUS_LABELS_BY_TYPE = {
  invoice: {
    pending: 'Pending',
    paid: 'Paid',
    overdue: 'Overdue',
    cancelled: 'Cancelled',
    draft: 'Draft',
    approved: 'Approved'
  },
  quote: {
    draft: 'Draft',
    sent: 'Sent to Client',
    approved: 'Approved',
    rejected: 'Rejected',
    expired: 'Expired',
    pending: 'Pending'
  },
  purchase_order: {
    pending: 'Pending',
    approved: 'Approved',
    in_progress: 'In Process',
    completed: 'Completed',
    cancelled: 'Cancelled',
    rejected: 'Rejected',
    paid: 'Paid',
    draft: 'Draft'
  }
};
const FALLBACK_DEFAULT_STATUS_BY_TYPE = {
  invoice: 'pending',
  quote: 'draft',
  purchase_order: 'pending'
};

const getStatusOptionsForType = (statusRules, type, currentStatus = '') => {
  const base = statusRules?.[type]?.allowed || FALLBACK_STATUS_OPTIONS_BY_TYPE[type] || FALLBACK_STATUS_OPTIONS_BY_TYPE.invoice;
  const normalizedCurrent = (currentStatus || '').toString().toLowerCase().trim();
  if (!normalizedCurrent || base.includes(normalizedCurrent)) return base;
  return [...base, normalizedCurrent];
};
const getStatusOptionsForTransition = ({
  statusRules,
  type,
  currentStatus,
  editMode,
  originalType,
  originalStatus
}) => {
  const allowedOptions = getStatusOptionsForType(statusRules, type, currentStatus);
  if (!editMode) return allowedOptions;

  const normalizedOriginalType = (originalType || '').toString().toLowerCase().trim();
  const normalizedOriginalStatus = (originalStatus || '').toString().toLowerCase().trim();
  const normalizedCurrentStatus = (currentStatus || '').toString().toLowerCase().trim();

  if (!normalizedOriginalStatus || normalizedOriginalType !== type) return allowedOptions;

  const transitionsByType = statusRules?.[type]?.transitions;
  const fallbackTransitionsByType = INITIAL_STATUS_CONFIG.rules?.[type]?.transitions || {};
  const transitions = transitionsByType && typeof transitionsByType === 'object'
    ? transitionsByType
    : fallbackTransitionsByType;

  const allowedNext = Array.isArray(transitions?.[normalizedOriginalStatus])
    ? transitions[normalizedOriginalStatus]
    : [];
  const allowedByTransition = new Set([
    normalizedOriginalStatus, 
    ...allowedNext,
    normalizedCurrentStatus
  ].filter(Boolean));

  const filtered = allowedOptions.filter((status) => allowedByTransition.has(status));
  return filtered.length ? filtered : allowedOptions;
};

const getDefaultStatusForType = (statusRules, type) => (
  statusRules?.[type]?.defaultStatus || FALLBACK_DEFAULT_STATUS_BY_TYPE[type] || 'pending'
);
const normalizeStatusValue = (status, type, statusRules) => {
  const normalized = (status || '').toString().toLowerCase().trim();
  return normalized || getDefaultStatusForType(statusRules, type);
};
const getStatusLabelForType = (labelsByType, type, statusValue) => {
  const normalized = (statusValue || '').toString().toLowerCase().trim();
  if (!normalized) return '-';
  const label = labelsByType?.[type]?.[normalized] || FALLBACK_STATUS_LABELS_BY_TYPE[type]?.[normalized];
  if (label) return label;
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

const addDaysISO = (dateString, days) => {
  const base = dateString ? new Date(dateString) : new Date();
  if (Number.isNaN(base.getTime())) return new Date().toISOString().split('T')[0];
  base.setDate(base.getDate() + days);
  return base.toISOString().split('T')[0];
};

const parseNotesPayload = (rawNotes) => {
  const notesText = (rawNotes || '').toString();
  if (!notesText.startsWith(PO_META_TAG)) {
    return { description: notesText, meta: {} };
  }

  const firstBreak = notesText.indexOf('\n');
  const metaRaw = firstBreak === -1 ? notesText.slice(PO_META_TAG.length) : notesText.slice(PO_META_TAG.length, firstBreak);
  const description = firstBreak === -1 ? '' : notesText.slice(firstBreak + 1).trim();

  try {
    const meta = JSON.parse(metaRaw);
    return { description, meta: meta && typeof meta === 'object' ? meta : {} };
  } catch (e) {
    return { description: notesText, meta: {} };
  }
};

const buildNotesPayload = (description, meta) => {
  const cleanDescription = (description || '').trim();
  return `${PO_META_TAG}${JSON.stringify(meta)}\n${cleanDescription}`;
};

let nextProductEditorKey = 1;
const createProductEditorKey = () => `product-editor-${Date.now()}-${nextProductEditorKey++}`;

const createEmptyProduct = () => ({
  editor_key: createProductEditorKey(),
  catalog_item_id: '',
  description: '',
  detail_description: '',
  technical_specs: '',
  item_code: '',
  quantity: '1',
  unit_price: ''
});

const normalizeProducts = (rawProducts) => {
  if (!Array.isArray(rawProducts) || !rawProducts.length) return [createEmptyProduct()];

  const normalized = rawProducts.map((item) => {
    const qtyRaw = Number(item?.quantity ?? item?.qty);
    const unitPriceRaw = Number(item?.unit_price ?? item?.unitPrice);
    return {
      editor_key: (item?.editor_key || createProductEditorKey()).toString(),
      catalog_item_id: (item?.catalog_item_id || item?.catalogItemId || '').toString(),
      description: (item?.description || item?.name || item?.title || '').toString(),
      detail_description: (
        item?.detail_description
        || item?.detailed_description
        || item?.long_description
        || ''
      ).toString(),
      technical_specs: Array.isArray(item?.technical_specs)
        ? item.technical_specs
          .map((spec) => `${spec?.label || spec?.name || ''}: ${spec?.value || ''}`.trim())
          .filter(Boolean)
          .join('\n')
        : (item?.technical_specs || item?.technical_data || '').toString(),
      item_code: (item?.item_code || item?.itemCode || item?.code || '').toString(),
      quantity: String(Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1),
      unit_price: String(Number.isFinite(unitPriceRaw) && unitPriceRaw >= 0 ? unitPriceRaw : '')
    };
  });

  return normalized.length ? normalized : [createEmptyProduct()];
};

const hasSavableProducts = (items) => {
  if (!Array.isArray(items) || !items.length) return false;

  return items.some((item) => {
    const quantity = Number(item?.quantity);
    const unitPrice = Number(item?.unit_price);
    const hasText = Boolean((item?.description || '').trim() || (item?.item_code || '').trim());
    return Number.isFinite(quantity) && quantity > 0 && (hasText || (Number.isFinite(unitPrice) && unitPrice > 0));
  });
};

const hasFiniteNonNegativeValue = (value) => (
  value !== null
  && value !== undefined
  && String(value).trim() !== ''
  && Number.isFinite(Number(value))
  && Number(value) >= 0
);

const MAX_DESCRIPTION_IMAGES = 6;

const DescriptionImagePreview = ({ invoiceId, sourceIndex, file, alt }) => {
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(Boolean(invoiceId && sourceIndex !== undefined));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl = '';

    const loadPreview = async () => {
      try {
        setFailed(false);
        setLoading(true);
        const blob = file || await invoiceService.getInvoiceDescriptionImage(invoiceId, sourceIndex);
        objectUrl = URL.createObjectURL(blob);
        if (active) setPreviewUrl(objectUrl);
      } catch (error) {
        if (active) setFailed(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    if (file || (invoiceId && sourceIndex !== undefined)) {
      loadPreview();
    } else {
      setLoading(false);
      setFailed(true);
    }

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, invoiceId, sourceIndex]);

  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: '16 / 10',
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: alpha('#64748b', 0.12),
        display: 'grid',
        placeItems: 'center'
      }}
    >
      {loading && <CircularProgress size={24} aria-label="Cargando vista previa" />}
      {!loading && failed && <ImageIcon color="disabled" sx={{ fontSize: 36 }} aria-hidden="true" />}
      {!loading && !failed && previewUrl && (
        <Box
          component="img"
          src={previewUrl}
          alt={alt}
          loading="lazy"
          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      )}
    </Box>
  );
};

export default function InvoiceFormDialog({ 
  open, 
  onClose, 
  onSave, 
  invoice = null,
  renewalMode = false,
  existingInvoices = [],
  nextDocumentNumbers = null,
  draftUserId = null,
  projectOptions = []
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const editMode = !!invoice;
  const [savingRecord, setSavingRecord] = useState(false);
  const savingRef = useRef(false);
  const [saveError, setSaveError] = useState('');
  const [editConflict, setEditConflict] = useState(false);
  const [requestKey, setRequestKey] = useState(newSaveRequestKey);

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [catalogItems, setCatalogItems] = useState([]);
  const [loadingCatalogItems, setLoadingCatalogItems] = useState(false);
  const [statusRules, setStatusRules] = useState(INITIAL_STATUS_CONFIG.rules);
  const [statusLabelsByType, setStatusLabelsByType] = useState(INITIAL_STATUS_CONFIG.labelsByType);
  const [originalStatusContext, setOriginalStatusContext] = useState({ type: '', status: '' });
  const initializedDialogKeyRef = useRef('');
  
  const [selectedFile, setSelectedFile] = useState(null); 
  const [selectedDescriptionImages, setSelectedDescriptionImages] = useState([]);
  const [existingDescriptionImages, setExistingDescriptionImages] = useState([]);
  const [descriptionImageError, setDescriptionImageError] = useState('');
  const [products, setProducts] = useState([createEmptyProduct()]);

  const [formData, setFormData] = useState({
    type: 'invoice',
    invoice_number: '',
    client_name: '',
    client_contact: '',
    supplier_name: '',
    supplier_contact: '',
    supplier_email: '',
    supplier_phone: '',
    supplier_address: '',
    address: '',
    phone: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: new Date().toISOString().split('T')[0],
    currency: 'MXN', 
    subtotal: '',
    tax: 16,
    discount_enabled: false,
    discount_percent: '',
    discount_amount: 0,
    total: '',
    status: 'pending',
    notes: '',
    customer_id: '',
    unit_price: '',
    quantity: '1',
    item_code: '',
    detail_description: '',
    lead_time: 'when needed',
    payment_terms: '30 Days',
    delivery_term: '',
    issuer_address_profile: 'mx',
    terms_text: DEFAULT_PO_TERMS,
    project_name: '',
    project_id: '',
  });

  const draftPayload = useMemo(() => ({ formData, products, selectedFile, selectedDescriptionImages, requestKey }), [formData, products, selectedFile, selectedDescriptionImages, requestKey]);
  const draft = useRecoverableDraft({ userId: draftUserId, scope: 'billing-new', active: open && !editMode, value: draftPayload,
    hasContent: Boolean(formData.client_name || formData.supplier_name || formData.notes || selectedFile || selectedDescriptionImages.length || products.some(p => p.description || p.detail_description || p.item_code || p.technical_specs || Number(p.unit_price))),
    onRestore: payload => { if (!payload?.formData || !Array.isArray(payload.products)) return; setFormData(payload.formData); setProducts(payload.products); setSelectedFile(payload.selectedFile || null); setSelectedDescriptionImages(payload.selectedDescriptionImages || []); setRequestKey(payload.requestKey || newSaveRequestKey()); }
  });
  const closeSafely = async () => {
    if (savingRef.current) return;
    try { await draft.flush(); } catch (error) { if (!window.confirm(`${error.message} ¿Cerrar sin guardar el último cambio local?`)) return; }
    onClose();
  };

  // A converted quote keeps its discount on the invoice, including later edits.
  const canEditDiscount = isQuoteOrPO(formData.type)
    || (formData.type === 'invoice' && editMode && Boolean(Number(invoice?.discount_enabled)));

  const fetchProjects = useCallback(async () => {
    try {
      setLoadingProjects(true);
      const response = await projectService.getProjects({
        lookup: 'billing_form',
        limit: 500,
        selected_id: invoice?.project_id || undefined
      });
      setProjects(response?.projects || response?.data || response || []);
    } catch (error) {
      console.error('Error loading projects list:', error);
    } finally {
      setLoadingProjects(false);
    }
  }, [invoice?.project_id]);

  useEffect(() => {
    if (!Array.isArray(projectOptions) || !projectOptions.length) return;
    setProjects(projectOptions);
  }, [projectOptions]);

  useEffect(() => {
    if (open) {
      if (Array.isArray(projectOptions) && projectOptions.length) {
        setProjects(projectOptions);
      } else {
        fetchProjects();
      }
      fetchCatalogItems();
      setSelectedFile(null);
      setSelectedDescriptionImages([]);
      setDescriptionImageError('');
    }
  }, [fetchProjects, open, projectOptions]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const loadStatusRules = async () => {
      try {
        const config = await invoiceService.getStatusRules();
        if (cancelled || !config) return;
        setStatusRules(config.rules || INITIAL_STATUS_CONFIG.rules);
        setStatusLabelsByType(config.labelsByType || INITIAL_STATUS_CONFIG.labelsByType);
      } catch (error) {
        if (cancelled) return;
        setStatusRules(INITIAL_STATUS_CONFIG.rules);
        setStatusLabelsByType(INITIAL_STATUS_CONFIG.labelsByType);
      }
    };

    loadStatusRules();
    return () => { cancelled = true; };
  }, [open]);

  const generateNextNumber = useCallback((type) => {
    const seededNextNumber = nextDocumentNumbers?.[type];
    if (seededNextNumber) return seededNextNumber;

    const prefixMap = { 'invoice': 'INV', 'quote': 'QT', 'purchase_order': 'PO' };
    const prefix = prefixMap[type] || 'DOC';
    const sameTypeDocs = existingInvoices.filter(doc => doc.type === type);

    if (sameTypeDocs.length === 0) return `${prefix}-001`;

    const numbers = sameTypeDocs.map(doc => {
        const invoiceNumber = String(doc.invoice_number || '');
        const match = invoiceNumber.match(/(\d+)(?!.*\d)/);
        return match ? parseInt(match[1], 10) : 0;
    });

    const maxNum = Math.max(...numbers, 0);
    return `${prefix}-${String(maxNum + 1).padStart(3, '0')}`;
  }, [existingInvoices, nextDocumentNumbers]);

  useEffect(() => {
    if (!open) {
      initializedDialogKeyRef.current = '';
      return;
    }

    const initializationKey = invoice?.id ? `edit:${invoice.id}` : `create:${draftUserId || ''}`;
    if (initializedDialogKeyRef.current === initializationKey) return;
    initializedDialogKeyRef.current = initializationKey;
    setSaveError(''); setEditConflict(false);

    if (open) {
      if (invoice) {
        const parsed = parseNotesPayload(invoice.notes || '');
        const issueDate = invoice.issue_date ? invoice.issue_date.split('T')[0] : new Date().toISOString().split('T')[0];
        const dueDate = invoice.due_date
          ? invoice.due_date.split('T')[0]
          : (isQuoteOrPO(invoice.type) ? addDaysISO(issueDate, AUTO_VALIDITY_DAYS) : issueDate);

        const parsedQuantityRaw = Number(parsed.meta.quantity ?? parsed.meta.qty);
        const parsedQuantity = Number.isFinite(parsedQuantityRaw) && parsedQuantityRaw > 0 ? parsedQuantityRaw : 1;
        const baseSubtotal = invoice.subtotal || ((invoice.total || 0) / 1.16).toFixed(2);
        const parsedUnitPriceRaw = Number(parsed.meta.unit_price);
        const parsedUnitPrice = Number.isFinite(parsedUnitPriceRaw)
          ? parsedUnitPriceRaw
          : ((parseFloat(baseSubtotal) || 0) / parsedQuantity);
        const parsedProducts = Array.isArray(parsed.meta.items) && parsed.meta.items.length
          ? normalizeProducts(parsed.meta.items)
          : normalizeProducts([{
            description: parsed.meta.detail_description || parsed.description || invoice.project_name || '',
            item_code: parsed.meta.item_code || '',
            quantity: parsedQuantity,
            unit_price: Number.isFinite(parsedUnitPrice) ? parsedUnitPrice : ''
          }]);
        const parsedDiscountPercentRaw = Number(
          invoice.discount_percent ?? parsed.meta.discount_percent ?? 0
        );
        const parsedDiscountPercent = Number.isFinite(parsedDiscountPercentRaw)
          ? Math.min(Math.max(parsedDiscountPercentRaw, 0), 100)
          : 0;
        const parsedDiscountEnabled = Boolean(
          Number(invoice.discount_enabled ?? parsed.meta.discount_enabled ?? 0)
          || parsedDiscountPercent > 0
        );
        const parsedDiscountAmountRaw = Number(
          invoice.discount_amount ?? parsed.meta.discount_amount
        );
        const parsedDiscountAmount = Number.isFinite(parsedDiscountAmountRaw)
          ? Math.max(parsedDiscountAmountRaw, 0)
          : ((Number(baseSubtotal) || 0) * parsedDiscountPercent / 100);

        setFormData({
          id: invoice.id,
          type: invoice.type,
          invoice_number: invoice.invoice_number,
          client_name: invoice.client_name,
          client_contact: invoice.client_contact || '',
          supplier_name: parsed.meta.supplier_name || '',
          supplier_contact: parsed.meta.supplier_contact || '',
          supplier_email: parsed.meta.supplier_email || '',
          supplier_phone: parsed.meta.supplier_phone || '',
          supplier_address: parsed.meta.supplier_address || '',
          address: invoice.address || parsed.meta.customer_fixed_address || (isQuoteOrPO(invoice.type) ? FIXED_CUSTOMER_ADDRESS : ''),
          phone: invoice.phone || '',
          issue_date: issueDate,
          due_date: dueDate,
          currency: invoice.currency || 'MXN', 
          subtotal: baseSubtotal,
          tax: invoice.tax ?? 16,
          discount_enabled: parsedDiscountEnabled,
          discount_percent: parsedDiscountEnabled ? String(parsedDiscountPercent) : '',
          discount_amount: parsedDiscountEnabled ? Number(parsedDiscountAmount.toFixed(2)) : 0,
          total: invoice.total ?? '',
          status: normalizeStatusValue(invoice.status, invoice.type, INITIAL_STATUS_CONFIG.rules),
          notes: parsed.description || '',
          customer_id: parsed.meta.customer_id || parsed.meta.customer_code || '',
          unit_price: String(Number.isFinite(parsedUnitPrice) ? parsedUnitPrice : ''),
          quantity: String(parsedQuantity),
          item_code: parsed.meta.item_code || parsed.meta.item_number || parsed.meta.sku || '',
          detail_description: parsed.meta.detail_description || parsed.meta.additional_description || '',
          lead_time: parsed.meta.lead_time || 'when needed',
          payment_terms: parsed.meta.payment_terms || '30 Days',
          delivery_term: parsed.meta.incoterm || parsed.meta.delivery_term || '',
          issuer_address_profile: invoice.issuer_entity === 'us_llc'
            ? 'us'
            : invoice.issuer_entity === 'mx_sa'
              ? 'mx'
              : (parsed.meta.issuer_address_profile || ''),
          terms_text: Array.isArray(parsed.meta.terms) && parsed.meta.terms.length > 0
            ? parsed.meta.terms.join('\n')
            : (parsed.meta.terms_text || DEFAULT_PO_TERMS),
          project_name: invoice.project_name || '',
          project_id: invoice.project_id ? String(invoice.project_id) : '',
        });
        setOriginalStatusContext({
          type: (invoice.type || '').toString().toLowerCase().trim(),
          status: (invoice.status || '').toString().toLowerCase().trim()
        });
        setProducts(parsedProducts);
        setExistingDescriptionImages(
          Array.isArray(parsed.meta.description_images)
            ? parsed.meta.description_images
              .map((storedPath) => String(storedPath || '').trim())
              .filter(Boolean)
              .map((storedPath, sourceIndex) => ({ storedPath, sourceIndex }))
            : []
        );
      } else {
        setRequestKey(newSaveRequestKey()); setSaveError('');
        const defaultType = 'invoice';
        const issueDate = new Date().toISOString().split('T')[0];
        setFormData({
          type: defaultType,
          invoice_number: generateNextNumber(defaultType), 
          client_name: '',
          client_contact: '',
          supplier_name: '',
          supplier_contact: '',
          supplier_email: '',
          supplier_phone: '',
          supplier_address: '',
          address: '',
          phone: '',
          issue_date: issueDate,
          due_date: issueDate,
          currency: 'MXN', 
          subtotal: '',
          tax: 16,
          discount_enabled: false,
          discount_percent: '',
          discount_amount: 0,
          total: '',
          status: getDefaultStatusForType(INITIAL_STATUS_CONFIG.rules, defaultType),
          notes: '',
          customer_id: '',
          unit_price: '',
          quantity: '1',
          item_code: '',
          detail_description: '',
          lead_time: 'when needed',
          payment_terms: '30 Days',
          delivery_term: '',
          issuer_address_profile: 'mx',
          terms_text: DEFAULT_PO_TERMS,
          project_name: '',
          project_id: '',
        });
        setProducts([createEmptyProduct()]);
        setOriginalStatusContext({ type: '', status: '' });
        setExistingDescriptionImages([]);
      }
    }
  }, [open, invoice, existingInvoices, generateNextNumber, draftUserId]);

  const handleTypeChange = (newType) => {
    const isPOOrQuote = isQuoteOrPO(newType);
    const supportsDiscount = isQuoteOrPO(newType)
      || (newType === 'invoice' && editMode && Boolean(Number(invoice?.discount_enabled)));
    if (!editMode) {
      setFormData(prev => ({
        ...prev,
        type: newType,
        invoice_number: generateNextNumber(newType),
        address: isQuoteOrPO(newType) ? (prev.address || FIXED_CUSTOMER_ADDRESS) : prev.address,
        due_date: (() => {
          const shouldSuggestNewValidity = !prev.due_date || prev.due_date === prev.issue_date;
          if (!isQuoteOrPO(newType)) return prev.due_date;
          return shouldSuggestNewValidity ? addDaysISO(prev.issue_date, AUTO_VALIDITY_DAYS) : prev.due_date;
        })(),
        status: (() => {
          const normalizedCurrent = (prev.status || '').toString().toLowerCase().trim();
          const validStatuses = statusRules?.[newType]?.allowed || FALLBACK_STATUS_OPTIONS_BY_TYPE[newType] || [];
          return validStatuses.includes(normalizedCurrent) ? normalizedCurrent : getDefaultStatusForType(statusRules, newType);
        })(),
        subtotal: isPOOrQuote ? calculateSubtotalFromProducts(products) : prev.subtotal,
        discount_enabled: supportsDiscount ? prev.discount_enabled : false,
        discount_percent: supportsDiscount ? prev.discount_percent : '',
        discount_amount: supportsDiscount
          ? calculateDiscountAmount(calculateSubtotalFromProducts(products), prev.discount_enabled, prev.discount_percent)
          : 0,
        total: isPOOrQuote
          ? calculateTotal(
            calculateSubtotalFromProducts(products),
            prev.tax,
            supportsDiscount && prev.discount_enabled,
            supportsDiscount ? prev.discount_percent : 0
          )
          : prev.total
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        type: newType,
        address: isQuoteOrPO(newType) ? (prev.address || FIXED_CUSTOMER_ADDRESS) : prev.address,
        due_date: (() => {
          const shouldSuggestNewValidity = !prev.due_date || prev.due_date === prev.issue_date;
          if (!isQuoteOrPO(newType)) return prev.due_date;
          return shouldSuggestNewValidity ? addDaysISO(prev.issue_date, AUTO_VALIDITY_DAYS) : prev.due_date;
        })(),
        status: (() => {
          const normalizedCurrent = (prev.status || '').toString().toLowerCase().trim();
          const validStatuses = statusRules?.[newType]?.allowed || FALLBACK_STATUS_OPTIONS_BY_TYPE[newType] || [];
          return validStatuses.includes(normalizedCurrent) ? normalizedCurrent : getDefaultStatusForType(statusRules, newType);
        })(),
        subtotal: isPOOrQuote ? calculateSubtotalFromProducts(products) : prev.subtotal,
        discount_enabled: supportsDiscount ? prev.discount_enabled : false,
        discount_percent: supportsDiscount ? prev.discount_percent : '',
        discount_amount: supportsDiscount
          ? calculateDiscountAmount(calculateSubtotalFromProducts(products), prev.discount_enabled, prev.discount_percent)
          : 0,
        total: isPOOrQuote
          ? calculateTotal(
            calculateSubtotalFromProducts(products),
            prev.tax,
            supportsDiscount && prev.discount_enabled,
            supportsDiscount ? prev.discount_percent : 0
          )
          : prev.total
      }));
    }
  };

  const fetchCatalogItems = async () => {
    try {
      setLoadingCatalogItems(true);
      const response = await invoiceService.getCatalogItems('', 200);
      setCatalogItems(response?.items || []);
    } catch (error) {
      console.error('Error loading billing catalog items:', error);
      setCatalogItems([]);
    } finally {
      setLoadingCatalogItems(false);
    }
  };

  const normalizeDiscountPercent = (value) => {
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.min(Math.max(parsed, 0), 100);
  };

  const calculateDiscountAmount = (subtotal, enabled, discountPercent) => {
    if (!enabled) return 0;
    const parsedSubtotal = Number.parseFloat(subtotal);
    const sub = Number.isFinite(parsedSubtotal) && parsedSubtotal >= 0 ? parsedSubtotal : 0;
    return Number((sub * (normalizeDiscountPercent(discountPercent) / 100)).toFixed(2));
  };

  const calculateTotal = (subtotal, tax, discountEnabled = false, discountPercent = 0) => {
    const parsedSubtotal = Number.parseFloat(subtotal);
    const parsedTax = Number.parseFloat(tax);
    const sub = Number.isFinite(parsedSubtotal) && parsedSubtotal >= 0 ? parsedSubtotal : 0;
    const taxRate = Number.isFinite(parsedTax) && parsedTax >= 0 ? parsedTax : 0;
    const discountAmount = calculateDiscountAmount(sub, discountEnabled, discountPercent);
    const discountedSubtotal = Math.max(sub - discountAmount, 0);
    const taxAmount = discountedSubtotal * (taxRate / 100);
    return (discountedSubtotal + taxAmount).toFixed(2);
  };

  const calculateSubtotalFromProducts = (items) => {
    if (!Array.isArray(items) || !items.length) return '0.00';
    const sum = items.reduce((acc, item) => {
      const unit = parseFloat(item?.unit_price);
      const qty = parseFloat(item?.quantity);
      if (!Number.isFinite(unit) || unit < 0 || !Number.isFinite(qty) || qty <= 0) return acc;
      return acc + (unit * qty);
    }, 0);
    return sum.toFixed(2);
  };

  const handleSubtotalChange = (value) => {
    if (isQuoteOrPO(formData.type)) return;
    const discountEnabled = canEditDiscount && formData.discount_enabled;
    const newTotal = calculateTotal(value, formData.tax, discountEnabled, formData.discount_percent);
    setFormData({ ...formData, subtotal: value, discount_amount: calculateDiscountAmount(value, discountEnabled, formData.discount_percent), total: newTotal });
  };

  const handleTaxChange = (value) => {
    if (isQuoteOrPO(formData.type)) {
      const calculatedSubtotal = calculateSubtotalFromProducts(products);
      const newTotal = calculateTotal(
        calculatedSubtotal,
        value,
        canEditDiscount && formData.discount_enabled,
        formData.discount_percent
      );
      setFormData({ ...formData, tax: value, subtotal: calculatedSubtotal, total: newTotal });
      return;
    }
    const newTotal = calculateTotal(formData.subtotal, value, canEditDiscount && formData.discount_enabled, formData.discount_percent);
    setFormData({ ...formData, tax: value, total: newTotal });
  };

  const handleDiscountToggle = (enabled) => {
    if (!canEditDiscount) return;
    setFormData((prev) => {
      const discountPercent = enabled ? prev.discount_percent : '';
      const discountAmount = calculateDiscountAmount(prev.subtotal, enabled, discountPercent);
      return {
        ...prev,
        discount_enabled: enabled,
        discount_percent: discountPercent,
        discount_amount: discountAmount,
        total: calculateTotal(prev.subtotal, prev.tax, enabled, discountPercent)
      };
    });
  };

  const handleDiscountPercentChange = (value) => {
    if (!canEditDiscount) return;
    const parsedValue = Number(value);
    const normalizedValue = value === ''
      ? ''
      : (Number.isFinite(parsedValue) ? String(Math.min(Math.max(parsedValue, 0), 100)) : formData.discount_percent);
    setFormData((prev) => ({
      ...prev,
      discount_percent: normalizedValue,
      discount_amount: calculateDiscountAmount(prev.subtotal, true, normalizedValue),
      total: calculateTotal(prev.subtotal, prev.tax, true, normalizedValue)
    }));
  };

  const syncFinancialsWithProducts = (nextProducts) => {
    setProducts(nextProducts);
    if (!isQuoteOrPO(formData.type)) return;

    const calculatedSubtotal = calculateSubtotalFromProducts(nextProducts);
    const firstProduct = nextProducts[0] || createEmptyProduct();
    setFormData((prev) => ({
      ...prev,
      unit_price: firstProduct.unit_price || '',
      quantity: firstProduct.quantity || '1',
      item_code: firstProduct.item_code || '',
      subtotal: calculatedSubtotal,
      discount_amount: calculateDiscountAmount(
        calculatedSubtotal,
        isQuoteOrPO(prev.type) && prev.discount_enabled,
        prev.discount_percent
      ),
      total: calculateTotal(
        calculatedSubtotal,
        prev.tax,
        isQuoteOrPO(prev.type) && prev.discount_enabled,
        prev.discount_percent
      )
    }));
  };

  const handleProductChange = (index, field, value) => {
    const nextProducts = products.map((product, idx) => (
      idx === index ? { ...product, [field]: value } : product
    ));
    syncFinancialsWithProducts(nextProducts);
  };

  const handleApplyCatalogItem = (index, catalogItemId) => {
    const selected = catalogItems.find((item) => String(item.id) === String(catalogItemId));
    if (!selected) {
      handleProductChange(index, 'catalog_item_id', '');
      return;
    }

    const nextProducts = products.map((product, idx) => (
      idx === index
        ? {
          ...product,
          catalog_item_id: String(selected.id),
          description: selected.description || product.description,
          item_code: selected.item_code || product.item_code,
          unit_price: String(selected.unit_price ?? product.unit_price ?? '')
        }
        : product
    ));
    syncFinancialsWithProducts(nextProducts);
  };

  const handleAddProduct = () => {
    syncFinancialsWithProducts([...products, createEmptyProduct()]);
  };

  const handleApplyProductTechnicalTemplate = (index) => {
    const nextProducts = products.map((product, idx) => (
      idx === index
        ? {
          ...product,
          detail_description: product.detail_description || ITEM_DETAIL_TEMPLATE,
          technical_specs: product.technical_specs || ITEM_TECHNICAL_SPECS_TEMPLATE
        }
        : product
    ));
    syncFinancialsWithProducts(nextProducts);
  };

  const handleRemoveProduct = (indexToRemove) => {
    if (products.length <= 1) return;
    const nextProducts = products.filter((_, index) => index !== indexToRemove);
    syncFinancialsWithProducts(nextProducts.length ? nextProducts : [createEmptyProduct()]);
  };

  const handleProjectSelect = (projectId) => {
    const selectedProject = projects.find((p) => String(p.id) === String(projectId));
    if (selectedProject) {
      const projectAddress = (
        selectedProject.client_address
        || selectedProject.address
        || ''
      ).toString().trim();

      setFormData(prev => ({
        ...prev,
        project_id: String(projectId),
        project_name: selectedProject.name || prev.project_name,
        client_name: selectedProject.client_company_name || selectedProject.company_name || prev.client_name,
        customer_id: prev.customer_id || String(
          selectedProject.client_code
          || selectedProject.client_identifier
          || selectedProject.client_id
          || ''
        ),
        address: projectAddress || prev.address || FIXED_CUSTOMER_ADDRESS,
      }));
    } else {
      setFormData(prev => ({ ...prev, project_id: '', project_name: '' }));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleDescriptionImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setSelectedDescriptionImages((prev) => {
      const availableSlots = Math.max(0, MAX_DESCRIPTION_IMAGES - existingDescriptionImages.length - prev.length);
      if (!availableSlots) {
        setDescriptionImageError(`Puedes usar hasta ${MAX_DESCRIPTION_IMAGES} imágenes. Quita o reemplaza una para continuar.`);
        return prev;
      }

      const next = [...prev];
      const seen = new Set(prev.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      let added = 0;

      files.forEach((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (!seen.has(key) && added < availableSlots) {
          seen.add(key);
          next.push(file);
          added += 1;
        }
      });

      if (files.length > added) {
        setDescriptionImageError(`Solo se agregaron ${added} imagen${added === 1 ? '' : 'es'}; el máximo total es ${MAX_DESCRIPTION_IMAGES}.`);
      } else {
        setDescriptionImageError('');
      }

      return next;
    });

    e.target.value = '';
  };

  const removeDescriptionImage = (indexToRemove) => {
    setSelectedDescriptionImages((prev) => prev.filter((_, index) => index !== indexToRemove));
    setDescriptionImageError('');
  };

  const removeExistingDescriptionImage = (entryToRemove) => {
    setExistingDescriptionImages((prev) => prev.filter((entry) => (
      entry.storedPath !== entryToRemove.storedPath || entry.sourceIndex !== entryToRemove.sourceIndex
    )));
    setDescriptionImageError('');
  };

  const replaceExistingDescriptionImage = (entryToReplace, event) => {
    const replacement = event.target.files?.[0];
    event.target.value = '';
    if (!replacement) return;

    setSelectedDescriptionImages((prev) => [...prev, replacement]);
    removeExistingDescriptionImage(entryToReplace);
  };

  const replaceSelectedDescriptionImage = (indexToReplace, event) => {
    const replacement = event.target.files?.[0];
    event.target.value = '';
    if (!replacement) return;

    setSelectedDescriptionImages((prev) => prev.map((file, index) => (
      index === indexToReplace ? replacement : file
    )));
    setDescriptionImageError('');
  };

  const appendMarkdownSnippetToField = (field, snippet) => {
    setFormData((prev) => {
      const current = (prev[field] || '').toString();
      const needsBreak = current.length > 0 && !current.endsWith('\n');
      return {
        ...prev,
        [field]: `${current}${needsBreak ? '\n' : ''}${snippet}`
      };
    });
  };

  // --- STYLING: Configuración de Selects modernos ---
  const selectMenuProps = {
    PaperProps: {
      elevation: 4,
      sx: {
        mt: 0.5,
        borderRadius: 2,
        minWidth: 200,
        maxWidth: isMobile ? 'calc(100vw - 32px)' : 560,
        maxHeight: isMobile ? 'min(50dvh, 420px)' : 480,
        '& .MuiMenuItem-root': {
          mx: 1,
          my: 0.5,
          borderRadius: 1,
          typography: 'body2',
          minHeight: 44,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          lineHeight: 1.35,
          transition: 'all 0.2s',
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
          '&.Mui-selected': { 
            bgcolor: alpha(theme.palette.primary.main, 0.12), 
            fontWeight: 700 
          }
        }
      }
    }
  };

  // --- STYLING: Estilos comunes de TextField para unificar diseño ---
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: alpha(theme.palette.background.paper, 0.6),
      transition: 'background-color 0.2s',
      '&:hover': { bgcolor: alpha(theme.palette.background.paper, 1) },
      '&.Mui-focused': { bgcolor: theme.palette.background.paper }
    }
  };

  const renderFormattingHelper = () => (
    <Grid item xs={12}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: alpha(theme.palette.info.main, 0.35),
          bgcolor: alpha(theme.palette.info.main, 0.04),
          borderRadius: 2,
          p: 2,
          boxShadow: `0 2px 8px ${alpha(theme.palette.info.main, 0.05)}`
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="info.main">
          Advanced formatting for PDF (Markdown)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Use these shortcuts in Description / Scope and Additional Description.
        </Typography>

        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, mb: 0.75, fontWeight: 700 }}>
          Insert into Description / Scope
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {MARKDOWN_FORMAT_SNIPPETS.map((item) => (
            <Button
              key={`notes-${item.label}`}
              size="small"
              variant="outlined"
              color="info"
              onClick={() => appendMarkdownSnippetToField('notes', item.snippet)}
              sx={{ minWidth: 'fit-content', textTransform: 'none', borderRadius: 1.5 }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, mb: 0.75, fontWeight: 700 }}>
          Insert into Additional Description
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {MARKDOWN_FORMAT_SNIPPETS.map((item) => (
            <Button
              key={`detail-${item.label}`}
              size="small"
              variant="text"
              color="info"
              onClick={() => appendMarkdownSnippetToField('detail_description', item.snippet)}
              sx={{ minWidth: 'fit-content', textTransform: 'none', borderRadius: 1.5 }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, mb: 0.5, fontWeight: 700 }}>
          Full example
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.25,
            borderRadius: 1.5,
            fontSize: '0.72rem',
            lineHeight: 1.5,
            overflowX: 'auto',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            bgcolor: alpha(theme.palette.background.default, 0.7),
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.6),
            whiteSpace: 'pre-wrap'
          }}
        >
          {MARKDOWN_ADVANCED_EXAMPLE}
        </Box>
      </Box>
    </Grid>
  );

  const handleSubmit = async () => {
    if (savingRef.current || draft.loading || draft.pending) return;
    if (!formData.client_name) return;
    if (!['mx', 'us'].includes(formData.issuer_address_profile)) return;
    if (formData.type === 'purchase_order' && !formData.supplier_name.trim()) return;
    if (isQuoteOrPO(formData.type) && !hasSavableProducts(products)) return;
    if (!isQuoteOrPO(formData.type) && !formData.subtotal) return;

    const payload = { ...formData };
    if (renewalMode) { payload.type = 'quote'; payload.status = 'draft'; payload.invoice_number = invoice.invoice_number; }
    if (editMode) payload.expected_revision = invoice?._edit_revision;
    else payload.client_request_id = requestKey;
    payload.issuer_entity = payload.issuer_address_profile === 'us' ? 'us_llc' : 'mx_sa';
    const parsedTax = Number(payload.tax);
    payload.tax = Number.isFinite(parsedTax) && parsedTax >= 0 ? parsedTax : 0;

    if (isQuoteOrPO(payload.type)) {
      payload.address = (payload.address || '').trim() || FIXED_CUSTOMER_ADDRESS;

      const manualTerms = (payload.terms_text || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      const normalizedProducts = products
        .map((product) => {
          const parsedUnitPrice = Number(product.unit_price);
          const parsedQuantity = Number(product.quantity);
          const safeUnitPrice = Number.isFinite(parsedUnitPrice) && parsedUnitPrice >= 0 ? parsedUnitPrice : 0;
          const safeQuantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
          const lineTotal = Number((safeUnitPrice * safeQuantity).toFixed(2));

          return {
            description: (product.description || '').trim(),
            detail_description: (product.detail_description || '').trim(),
            technical_specs: (product.technical_specs || '').trim(),
            item_code: (product.item_code || '').trim(),
            quantity: safeQuantity,
            unit_price: safeUnitPrice,
            line_total: lineTotal
          };
        })
        .filter((item) => (
          item.quantity > 0
          && (item.unit_price > 0 || item.description || item.item_code)
        ));
      const productsToSave = normalizedProducts.length
        ? normalizedProducts
        : [{
          description: (payload.notes || '').trim(),
          item_code: '',
          quantity: 1,
          unit_price: 0,
          line_total: 0
        }];
      const firstProduct = productsToSave[0];
      const calculatedSubtotal = productsToSave
        .reduce((acc, item) => acc + (Number(item.line_total) || 0), 0)
        .toFixed(2);
      const discountEnabled = isQuoteOrPO(payload.type) && Boolean(payload.discount_enabled);
      const discountPercent = discountEnabled
        ? normalizeDiscountPercent(payload.discount_percent)
        : 0;
      const discountAmount = calculateDiscountAmount(
        calculatedSubtotal,
        discountEnabled,
        discountPercent
      );

      payload.subtotal = calculatedSubtotal;
      payload.discount_enabled = discountEnabled;
      payload.discount_percent = discountPercent;
      payload.discount_amount = discountAmount;
      payload.total = calculateTotal(
        calculatedSubtotal,
        payload.tax,
        discountEnabled,
        discountPercent
      );
      payload.unit_price = String(firstProduct.unit_price || '');
      payload.quantity = String(firstProduct.quantity || '1');
      payload.item_code = firstProduct.item_code || '';

      payload.notes = buildNotesPayload(payload.notes, {
        customer_id: payload.customer_id || '',
        supplier_name: payload.supplier_name?.trim() || '',
        supplier_contact: payload.supplier_contact?.trim() || '',
        supplier_email: payload.supplier_email?.trim() || '',
        supplier_phone: payload.supplier_phone?.trim() || '',
        supplier_address: payload.supplier_address?.trim() || '',
        unit_price: firstProduct.unit_price || 0,
        quantity: firstProduct.quantity || 1,
        item_code: firstProduct.item_code || '',
        items: productsToSave,
        discount_enabled: discountEnabled,
        discount_percent: discountPercent,
        discount_amount: discountAmount,
        detail_description: payload.detail_description || '',
        lead_time: payload.lead_time || '',
        payment_terms: payload.payment_terms || '',
        incoterm: payload.delivery_term || '',
        delivery_term: payload.delivery_term || '',
        issuer_address_profile: payload.issuer_address_profile || 'mx',
        issuer_entity: payload.issuer_entity,
        customer_fixed_address: payload.address,
        terms: manualTerms,
        terms_text: payload.terms_text || '',
        description_images: existingDescriptionImages.map((entry) => entry.storedPath)
      });
    } else if (payload.type === 'invoice') {
      if (canEditDiscount) {
        const discountEnabled = Boolean(payload.discount_enabled);
        const discountPercent = discountEnabled ? normalizeDiscountPercent(payload.discount_percent) : 0;
        payload.discount_enabled = discountEnabled;
        payload.discount_percent = discountPercent;
        payload.discount_amount = calculateDiscountAmount(payload.subtotal, discountEnabled, discountPercent);
        payload.total = calculateTotal(payload.subtotal, payload.tax, discountEnabled, discountPercent);
      }
      if (editMode && invoice?.notes?.startsWith(PO_META_TAG)) {
        payload.notes = buildNotesPayload(payload.notes, {
          ...parseNotesPayload(invoice.notes).meta,
          ...(canEditDiscount ? {
            discount_enabled: payload.discount_enabled,
            discount_percent: payload.discount_percent,
            discount_amount: payload.discount_amount
          } : {})
        });
      }
    }

    savingRef.current = true; setSavingRecord(true); setSaveError(''); setEditConflict(false);
    try {
      const result = await onSave(payload, selectedFile, selectedDescriptionImages);
      if (result === true || result?.success) { await draft.clear().catch(() => {}); onClose(); }
    } catch (error) { setEditConflict(error.response?.data?.code === 'EDIT_CONFLICT'); setSaveError(error.response?.data?.message || error.message || 'No se pudo guardar. Conservamos el formulario.'); }
    finally { savingRef.current = false; setSavingRecord(false); }
  };

  const getIcon = () => {
    switch(formData.type) {
      case 'purchase_order': return <POIcon />;
      case 'quote': return <QuoteIcon />;
      default: return <InvoiceIcon />;
    }
  };

  const getHeaderColor = () => {
    switch(formData.type) {
      case 'purchase_order': return 'secondary.main';
      case 'quote': return 'warning.main';
      default: return 'primary.main';
    }
  };

  const displayTotal = () => {
    const amount = parseFloat(formData.total) || 0;
    return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: formData.currency 
    }).format(amount);
  };

  const displayMoney = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: formData.currency
  }).format(Number(value) || 0);

  const statusOptionsForSelector = getStatusOptionsForTransition({
    statusRules,
    type: formData.type,
    currentStatus: formData.status,
    editMode,
    originalType: originalStatusContext.type,
    originalStatus: originalStatusContext.status
  });
  const isTransitionConstrained = editMode
    && originalStatusContext.type === formData.type
    && Boolean(originalStatusContext.status);
  const hasValidTotal = hasFiniteNonNegativeValue(formData.total);
  const hasValidSubtotal = hasFiniteNonNegativeValue(formData.subtotal);
  const hasValidIssuerEntity = ['mx', 'us'].includes(formData.issuer_address_profile);
  const discountPercentValue = Number(formData.discount_percent);
  const hasValidDiscount = !canEditDiscount
    || !formData.discount_enabled
    || (Number.isFinite(discountPercentValue) && discountPercentValue > 0 && discountPercentValue <= 100);
  const canSave = Boolean(
    formData.client_name
    && hasValidIssuerEntity
    && (formData.type !== 'purchase_order' || formData.supplier_name.trim())
    && hasValidTotal
    && hasValidDiscount
    && (
      isQuoteOrPO(formData.type)
        ? hasSavableProducts(products)
        : hasValidSubtotal
    )
  );
  const projectSelectorOptions = [
    { id: '', name: 'No Project (Manual Entry)', manualEntry: true },
    ...projects
  ];
  const selectedProjectOption = projectSelectorOptions.find(
    (project) => String(project.id) === String(formData.project_id || '')
  ) || projectSelectorOptions[0];
  const catalogSelectorOptions = [
    { id: '', item_code: '', description: 'None / Manual capture', manualEntry: true },
    ...catalogItems
  ];
  const catalogItemLabel = (item) => {
    if (!item) return '';
    if (item.manualEntry) return item.description;
    return `${item.item_code ? `${item.item_code} - ` : ''}${item.description || 'Unnamed item'}`;
  };

  return (
    <Dialog 
      open={open} 
      onClose={closeSafely}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      slotProps={{ paper: {
        sx: { 
          borderRadius: isMobile ? 0 : 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          ...(isMobile && {
            m: 0,
            width: '100%',
            maxWidth: '100%',
            height: '100dvh',
            maxHeight: '100dvh',
            overflow: 'hidden'
          })
        } 
      } }}
    >
      <DialogTitle sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: { xs: 2, sm: 3 },
        pt: isMobile ? 'max(16px, env(safe-area-inset-top, 0px))' : 2.5,
        pb: { xs: 1.5, sm: 2 },
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 2 }, minWidth: 0 }}>
          <Avatar sx={{ bgcolor: getHeaderColor(), boxShadow: 2, width: { xs: 42, sm: 48 }, height: { xs: 42, sm: 48 }, flexShrink: 0 }}>
            {editMode ? <EditIcon /> : getIcon()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: { xs: '1.12rem', sm: '1.25rem' } }}>
              {renewalMode ? 'Renovar QT' : editMode ? 'Edit Document' : 'Register Document'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
              {formData.type === 'invoice' ? 'Invoice' : formData.type === 'quote' ? 'Quotation' : 'Purchase Order'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 },
        bgcolor: alpha(theme.palette.background.default, 0.3),
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain'
      }}>
        <Box sx={{ mb: 2 }}><RecoverableDraftNotice draft={draft} />{editConflict && <EditConflictInspector resource="invoices" baseline={invoice?._renewal_baseline || invoice} localRecord={{ ...formData, _products: products }} />}{saveError && <Alert severity="error" sx={{ mt: 1 }}>{saveError}</Alert>}</Box>
        {renewalMode && <Alert severity="info" sx={{ mb: 2 }}>Revisa las nuevas fechas y el contenido. Al guardar se conservará el mismo folio como borrador y podrás comparar los cambios con la versión anterior.</Alert>}
        <Box component="fieldset" disabled={savingRecord || draft.loading || Boolean(draft.pending)} inert={savingRecord || draft.loading || Boolean(draft.pending) ? true : undefined} sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
        <Grid container spacing={{ xs: 1.75, sm: 2 }}>
          
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Document Type"
              disabled={renewalMode}
              value={formData.type}
              onChange={(e) => handleTypeChange(e.target.value)}
              size="small"
              SelectProps={{ MenuProps: selectMenuProps }}
              sx={inputSx}
            >
              <MenuItem value="invoice">Invoice</MenuItem>
              <MenuItem value="quote">Quotation</MenuItem>
              <MenuItem value="purchase_order">Purchase Order (PO)</MenuItem>
            </TextField>
          </Grid>

          {/* ✅ CAMPO DE FOLIO MANUAL */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Folio / Document #"
              disabled={renewalMode}
              value={formData.invoice_number}
              onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
              size="small"
              placeholder="e.g., PO-2026-001"
              helperText="Enter the official document number manually"
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>LINK TO PROJECT (Optional)</Typography>
            <Autocomplete
              fullWidth
              options={projectSelectorOptions}
              value={selectedProjectOption}
              onChange={(_event, project) => handleProjectSelect(project?.id || '')}
              getOptionLabel={(project) => String(project?.name || '')}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              loading={loadingProjects}
              autoHighlight
              openOnFocus
              selectOnFocus
              blurOnSelect
              noOptionsText="No matching projects"
              slotProps={{
                paper: {
                  elevation: 5,
                  sx: {
                    mt: 0.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                  }
                },
                listbox: {
                  sx: {
                    maxHeight: { xs: 'min(46dvh, 360px)', sm: 420 },
                    py: 0.5,
                    '& .MuiAutocomplete-option': {
                      minHeight: 48,
                      mx: 0.75,
                      my: 0.25,
                      px: 1.25,
                      borderRadius: 1.25,
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                      lineHeight: 1.35
                    }
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search or select project"
                  placeholder="Type a project name"
                  size="small"
                  sx={inputSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start"><ProjectIcon fontSize="small" color="action" /></InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {loadingProjects && <CircularProgress color="inherit" size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={formData.type === 'purchase_order' ? 'Project / End Customer' : 'Client / Vendor Name'}
              value={formData.client_name}
              onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
              required
              size="small"
              placeholder={formData.type === 'purchase_order' ? 'Customer or project related to this PO' : 'Who issued this document?'}
              sx={inputSx}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" color="action" /></InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={formData.type === 'purchase_order' ? 'Project Customer Contact' : 'Customer Contact'}
              value={formData.client_contact}
              onChange={(e) => setFormData({ ...formData, client_contact: e.target.value })}
              size="small"
              sx={inputSx}
              placeholder="e.g. Ing. Hugo Rodriguez"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={formData.type === 'purchase_order' ? 'Project Customer Phone' : 'Customer Phone'}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              size="small"
              sx={inputSx}
              placeholder="e.g. +52..."
            />
          </Grid>

          {isQuoteOrPO(formData.type) && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={formData.type === 'purchase_order' ? 'Project / Customer Address' : 'Customer Address (From Project)'}
                value={formData.address || FIXED_CUSTOMER_ADDRESS}
                size="small"
                sx={inputSx}
                InputProps={{ readOnly: true }}
                helperText="Filled automatically when a project is selected; it can be changed in the client or project record."
              />
            </Grid>
          )}

          {formData.type === 'purchase_order' && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  border: '1px solid',
                  borderColor: alpha(theme.palette.secondary.main, 0.35),
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.06)
                }}
              >
                <Typography variant="subtitle2" color="secondary.main" sx={{ fontWeight: 800 }}>
                  PURCHASE ORDER RECIPIENT
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Supplier or vendor receiving this purchase order.
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      size="small"
                      label="Supplier / Vendor Name"
                      value={formData.supplier_name}
                      onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                      placeholder="Company receiving the PO"
                      helperText="This company will appear as the PO recipient."
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Supplier Contact"
                      value={formData.supplier_contact}
                      onChange={(e) => setFormData({ ...formData, supplier_contact: e.target.value })}
                      placeholder="Contact person's name"
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="email"
                      label="Supplier Email"
                      value={formData.supplier_email}
                      onChange={(e) => setFormData({ ...formData, supplier_email: e.target.value })}
                      placeholder="purchasing@supplier.com"
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="tel"
                      label="Supplier Phone"
                      value={formData.supplier_phone}
                      onChange={(e) => setFormData({ ...formData, supplier_phone: e.target.value })}
                      placeholder="e.g. +52..."
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Supplier Address"
                      value={formData.supplier_address}
                      onChange={(e) => setFormData({ ...formData, supplier_address: e.target.value })}
                      placeholder="Street, city, state, postal code, country"
                      sx={inputSx}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Issue Date"
              value={formData.issue_date}
              onChange={(e) => {
                const nextIssueDate = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  issue_date: nextIssueDate,
                  due_date: prev.due_date || (isQuoteOrPO(prev.type) ? addDaysISO(nextIssueDate, AUTO_VALIDITY_DAYS) : prev.due_date)
                }));
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Due Date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={inputSx}
              helperText={isQuoteOrPO(formData.type) ? `Suggested: ${AUTO_VALIDITY_DAYS} days after the issue date (editable)` : ''}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Issuing legal entity"
              value={formData.issuer_address_profile}
              onChange={(e) => setFormData({ ...formData, issuer_address_profile: e.target.value })}
              size="small"
              SelectProps={{ MenuProps: selectMenuProps }}
              sx={inputSx}
              error={!hasValidIssuerEntity}
              helperText={hasValidIssuerEntity
                ? 'Required for the document header and entity-specific financial indicators.'
                : 'Select the legal entity that issued this document.'}
            >
              <MenuItem value="" disabled>Select legal entity</MenuItem>
              {ISSUER_ADDRESS_PROFILES.map((profile) => (
                <MenuItem key={profile.value} value={profile.value}>{profile.label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {isQuoteOrPO(formData.type) && (
            <>
              <Grid item xs={12}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: alpha(theme.palette.background.paper, 0.5), boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.03)}` }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 700 }}>
                    COMMERCIAL TERMS (MANUAL)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Customer ID"
                        value={formData.customer_id}
                        onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                        size="small"
                        sx={inputSx}
                        placeholder="e.g. SLA-001"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: theme.palette.background.paper }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            PRODUCTS
                          </Typography>
                          <Button size="small" onClick={handleAddProduct} startIcon={<AddIcon />} sx={{ textTransform: 'none' }}>Add Product</Button>
                        </Box>
                        <Grid container spacing={1}>
                          {products.map((product, index) => (
                            <React.Fragment key={product.editor_key}>
                              <Grid item xs={12}>
                                <Autocomplete
                                  fullWidth
                                  options={catalogSelectorOptions}
                                  value={catalogSelectorOptions.find(
                                    (item) => String(item.id) === String(product.catalog_item_id || '')
                                  ) || catalogSelectorOptions[0]}
                                  onChange={(_event, item) => handleApplyCatalogItem(index, item?.id || '')}
                                  getOptionLabel={catalogItemLabel}
                                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                                  loading={loadingCatalogItems}
                                  autoHighlight
                                  openOnFocus
                                  selectOnFocus
                                  blurOnSelect
                                  noOptionsText="No matching items"
                                  slotProps={{
                                    paper: {
                                      elevation: 5,
                                      sx: {
                                        mt: 0.5,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider'
                                      }
                                    },
                                    listbox: {
                                      sx: {
                                        maxHeight: { xs: 'min(46dvh, 360px)', sm: 420 },
                                        py: 0.5,
                                        '& .MuiAutocomplete-option': {
                                          minHeight: 48,
                                          mx: 0.75,
                                          my: 0.25,
                                          px: 1.25,
                                          borderRadius: 1.25,
                                          whiteSpace: 'normal',
                                          overflowWrap: 'anywhere',
                                          lineHeight: 1.35
                                        }
                                      }
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      label={`Search saved item #${index + 1}`}
                                      placeholder="Type a code or description"
                                      helperText="Select an item to autocomplete its description, code and price"
                                      sx={inputSx}
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <>
                                            {loadingCatalogItems && <CircularProgress color="inherit" size={18} />}
                                            {params.InputProps.endAdornment}
                                          </>
                                        )
                                      }}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12} sm={8}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label={`Product / model #${index + 1}`}
                                  value={product.description}
                                  onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                                  placeholder="e.g. SERIO 4000.1 Stencil printer"
                                  helperText="Short title displayed at the beginning of the item"
                                  sx={inputSx}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Code"
                                  value={product.item_code}
                                  onChange={(e) => handleProductChange(index, 'item_code', e.target.value)}
                                  placeholder="SKU"
                                  sx={inputSx}
                                />
                              </Grid>
                              <Grid item xs={12} md={7}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={5}
                                  label="Detailed item description"
                                  value={product.detail_description}
                                  onChange={(e) => handleProductChange(index, 'detail_description', e.target.value)}
                                  placeholder="Explain the equipment, service, functions and included scope."
                                  helperText="Supports **bold**, *italic*, headings, lists and paragraphs in the PDF."
                                  sx={inputSx}
                                />
                              </Grid>
                              <Grid item xs={12} md={5}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={5}
                                  label="Technical data"
                                  value={product.technical_specs}
                                  onChange={(e) => handleProductChange(index, 'technical_specs', e.target.value)}
                                  placeholder={'Print format: Min. 80 x 50 mm\nPrint speed: 2 to 200 mm/s'}
                                  helperText="Enter one specification per line using Label: value."
                                  sx={inputSx}
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="QTY"
                                  value={product.quantity}
                                  onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                                  inputProps={{ min: 1, step: 'any' }}
                                  sx={inputSx}
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Unit Price"
                                  value={product.unit_price}
                                  onChange={(e) => handleProductChange(index, 'unit_price', e.target.value)}
                                  inputProps={{ min: 0, step: 'any' }}
                                  sx={inputSx}
                                />
                              </Grid>
                              <Grid item xs={8} sm={4}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  startIcon={<QuoteIcon />}
                                  onClick={() => handleApplyProductTechnicalTemplate(index)}
                                  sx={{ minHeight: 44, textTransform: 'none' }}
                                >
                                  Load technical template
                                </Button>
                              </Grid>
                              <Grid item xs={4} sm={2}>
                                <IconButton
                                  color="error"
                                  aria-label={`Remove product ${index + 1}`}
                                  onClick={() => handleRemoveProduct(index)}
                                  disabled={products.length <= 1}
                                  sx={{ width: '100%', minHeight: 44, borderRadius: 1, border: '1px solid', borderColor: 'error.light', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Grid>
                              
                              {/* Divisor sutil si hay más de un producto, excepto para el último */}
                              {index < products.length - 1 && (
                                <Grid item xs={12}>
                                  <Box sx={{ borderBottom: '1px dashed', borderColor: 'divider', my: 1 }} />
                                </Grid>
                              )}
                            </React.Fragment>
                          ))}
                        </Grid>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Lead Time"
                        value={formData.lead_time}
                        onChange={(e) => setFormData({ ...formData, lead_time: e.target.value })}
                        size="small"
                        sx={inputSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Term"
                        value={formData.payment_terms}
                        onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                        size="small"
                        sx={inputSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Incoterm"
                        value={formData.delivery_term}
                        onChange={(e) => setFormData({ ...formData, delivery_term: e.target.value })}
                        size="small"
                        sx={inputSx}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Description / Scope (Manual)"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Additional Description"
                  value={formData.detail_description}
                  onChange={(e) => setFormData({ ...formData, detail_description: e.target.value })}
                  helperText="Additional text for the DESCRIPTION section of the PDF"
                  sx={inputSx}
                />
              </Grid>

              {renderFormattingHelper()}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  label="Terms and Conditions (Manual, one line per term)"
                  value={formData.terms_text}
                  onChange={(e) => setFormData({ ...formData, terms_text: e.target.value })}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '1px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    p: 2.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
                  }}
                  onClick={() => document.getElementById('description-images-upload').click()}
                >
                  <input
                    id="description-images-upload"
                    type="file"
                    hidden
                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                    multiple
                    onChange={handleDescriptionImagesChange}
                  />
                  <AddIcon color="primary" sx={{ fontSize: 28, mb: 1 }} />
                  <Typography variant="body2" fontWeight={700} color="primary">
                    Add Description Images
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    PNG o JPG/JPEG. Máximo {MAX_DESCRIPTION_IMAGES} imágenes entre guardadas y nuevas.
                  </Typography>
                </Box>
              </Grid>

              {descriptionImageError && (
                <Grid item xs={12}>
                  <Alert severity="warning" onClose={() => setDescriptionImageError('')}>
                    {descriptionImageError}
                  </Alert>
                </Grid>
              )}

              {existingDescriptionImages.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Imágenes guardadas ({existingDescriptionImages.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Puedes reemplazarlas o quitarlas de este documento. Si es una copia, la cotización original no cambia.
                  </Typography>
                  <Grid container spacing={1.5}>
                    {existingDescriptionImages.map((entry, index) => (
                      <Grid item xs={12} sm={6} md={4} key={`${entry.storedPath}-${entry.sourceIndex}`}>
                        <Box
                          sx={{
                            p: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.background.paper, 0.72)
                          }}
                        >
                          <DescriptionImagePreview
                            invoiceId={invoice?.id}
                            sourceIndex={entry.sourceIndex}
                            alt={`Imagen guardada ${index + 1} de la cotización`}
                          />
                          <Typography variant="body2" fontWeight={700} sx={{ mt: 1, mb: 0.75 }}>
                            Imagen {index + 1}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              component="label"
                              size="small"
                              variant="outlined"
                              startIcon={<ReplaceIcon aria-hidden="true" />}
                              sx={{ minHeight: 44, flex: '1 1 120px' }}
                            >
                              Reemplazar
                              <input
                                hidden
                                type="file"
                                accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                                onChange={(event) => replaceExistingDescriptionImage(entry, event)}
                              />
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="text"
                              startIcon={<DeleteIcon aria-hidden="true" />}
                              onClick={() => removeExistingDescriptionImage(entry)}
                              sx={{ minHeight: 44, flex: '1 1 96px' }}
                            >
                              Quitar
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}

              {selectedDescriptionImages.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ display: 'block', mb: 1, fontWeight: 700 }}>
                    Imágenes nuevas ({selectedDescriptionImages.length})
                  </Typography>
                  <Grid container spacing={1.5}>
                    {selectedDescriptionImages.map((image, index) => (
                      <Grid item xs={12} sm={6} md={4} key={`${image.name}-${image.size}-${image.lastModified}-${index}`}>
                        <Box
                          sx={{
                            p: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.background.paper, 0.8)
                          }}
                        >
                          <DescriptionImagePreview file={image} alt={`Vista previa de ${image.name}`} />
                          <Typography variant="body2" noWrap title={image.name} sx={{ mt: 1, mb: 0.75, fontWeight: 600 }}>
                            {image.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              component="label"
                              size="small"
                              variant="outlined"
                              startIcon={<ReplaceIcon aria-hidden="true" />}
                              sx={{ minHeight: 44, flex: '1 1 120px' }}
                            >
                              Reemplazar
                              <input
                                hidden
                                type="file"
                                accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                                onChange={(event) => replaceSelectedDescriptionImage(index, event)}
                              />
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon aria-hidden="true" />}
                              onClick={() => removeDescriptionImage(index)}
                              sx={{ minHeight: 44, flex: '1 1 96px' }}
                            >
                              Quitar
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {/* ✅ ZONA DE CARGA DEL ARCHIVO OFICIAL */}
          <Grid item xs={12}>
              <Box 
                  sx={{ 
                      border: '2px dashed #1976d2',
                      borderRadius: 2, 
                      p: { xs: 2, sm: 3 },
                      minHeight: { xs: 160, sm: 180 },
                      textAlign: 'center', 
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12), borderColor: theme.palette.primary.dark }
                  }}
                  onClick={() => document.getElementById('invoice-file-upload').click()}
              >
                  <input 
                      id="invoice-file-upload" 
                      type="file" 
                      hidden 
                      accept="application/pdf,image/*"
                      onChange={handleFileChange}
                  />
                  <CloudUploadIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body1" fontWeight={700} color="primary" sx={{ overflowWrap: 'anywhere' }}>
                      {selectedFile ? selectedFile.name : (invoice?.file_path ? "Replace Official Document" : "Upload Official Document (PDF)")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {selectedFile ? "Ready to upload" : "This file will be used as the official record."}
                  </Typography>
              </Box>
          </Grid>

          {/* SECCIÓN DE COSTOS */}
          <Grid item xs={12}>
            <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: theme.palette.background.paper, boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.03)}` }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', fontWeight: 700 }}>FINANCIAL DETAILS</Typography>
              <Grid container spacing={2}>
                
                <Grid item xs={12}>
                    <TextField
                        select
                        fullWidth
                        label="Currency"
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        size="small"
                        SelectProps={{ MenuProps: selectMenuProps }}
                        sx={inputSx}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><CurrencyIcon fontSize="small" /></InputAdornment>,
                        }}
                    >
                        <MenuItem value="MXN">🇲🇽 MXN (Mexican Peso)</MenuItem>
                        <MenuItem value="USD">🇺🇸 USD (US Dollar)</MenuItem>
                    </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Subtotal"
                    value={formData.subtotal}
                    onChange={(e) => handleSubtotalChange(e.target.value)}
                    size="small"
                    sx={inputSx}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      readOnly: isQuoteOrPO(formData.type)
                    }}
                    helperText={isQuoteOrPO(formData.type) ? 'Calculated automatically' : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Tax %"
                    value={formData.tax}
                    onChange={(e) => handleTaxChange(e.target.value)}
                    size="small"
                    sx={inputSx}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  />
                </Grid>
                {canEditDiscount && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: formData.discount_enabled
                          ? alpha(theme.palette.warning.main, 0.45)
                          : 'divider',
                        bgcolor: formData.discount_enabled
                          ? alpha(theme.palette.warning.main, 0.07)
                          : alpha(theme.palette.background.default, 0.45)
                      }}
                    >
                      <FormControlLabel
                        sx={{ m: 0, minHeight: 44, alignItems: 'center' }}
                        control={(
                          <Switch
                            checked={Boolean(formData.discount_enabled)}
                            onChange={(event) => handleDiscountToggle(event.target.checked)}
                            inputProps={{ 'aria-label': `Apply discount to ${formData.type === 'quote' ? 'quotation' : formData.type === 'purchase_order' ? 'purchase order' : 'invoice'}` }}
                            color="warning"
                          />
                        )}
                        label={(
                          <Box>
                            <Typography variant="body2" fontWeight={700}>Apply discount</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Optional. The discount is calculated before tax.
                            </Typography>
                          </Box>
                        )}
                      />

                      {formData.discount_enabled && (
                        <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Discount percentage"
                              value={formData.discount_percent}
                              onChange={(event) => handleDiscountPercentChange(event.target.value)}
                              size="small"
                              sx={inputSx}
                              inputProps={{ min: 0, max: 100, step: 0.01, inputMode: 'decimal' }}
                              error={!hasValidDiscount}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <DiscountIcon fontSize="small" aria-hidden="true" />
                                  </InputAdornment>
                                ),
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                              }}
                              helperText={hasValidDiscount ? 'Enter a value greater than 0 and up to 100' : 'Enter a discount greater than 0 and up to 100%'}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box
                              sx={{
                                minHeight: 44,
                                px: 1.5,
                                py: 1,
                                borderRadius: 1.25,
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">Discount amount</Typography>
                              <Typography variant="body2" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                -{displayMoney(formData.discount_amount)}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: alpha(theme.palette.primary.main, 0.1), p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.2) }}>
                    <Typography variant="body1" fontWeight={600}>Total Amount:</Typography>
                    <Typography variant="h6" fontWeight={800} color="primary.main">
                        {displayTotal()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {formData.type === 'invoice' && (
            <>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Description / Scope (Manual)"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Additional Description"
                  value={formData.detail_description}
                  onChange={(e) => setFormData({ ...formData, detail_description: e.target.value })}
                  helperText="Extra text for the DESCRIPTION section in the PDF"
                  sx={inputSx}
                />
              </Grid>

              {renderFormattingHelper()}
            </>
          )}

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Document Status"
              disabled={renewalMode}
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              size="small"
              SelectProps={{ MenuProps: selectMenuProps }}
              sx={inputSx}
              helperText={isTransitionConstrained ? 'Only statuses valid for this transition are shown.' : ''}
            >
              {statusOptionsForSelector.map((statusValue) => (
                <MenuItem key={statusValue} value={statusValue}>
                  {getStatusLabelForType(statusLabelsByType, formData.type, statusValue)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

        </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: { xs: 2, sm: 2.5 },
        pt: 1.5,
        pb: isMobile ? 'max(12px, env(safe-area-inset-bottom, 0px))' : 2.5,
        gap: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: theme.palette.background.paper,
        flexShrink: 0,
        '& .MuiButton-root': { minHeight: 44 },
        '& .MuiButton-root + .MuiButton-root': { ml: 0 }
      }}>
        <Button onClick={closeSafely} disabled={savingRecord} color="inherit" sx={{ fontWeight: 600, flex: { xs: 1, sm: 'initial' } }}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          startIcon={<SaveIcon />}
          disabled={!canSave || savingRecord || draft.loading || Boolean(draft.pending)}
          sx={{ borderRadius: 1.5, px: 3, py: 1, fontWeight: 700, flex: { xs: 1.4, sm: 'initial' } }}
        >
          {savingRecord ? 'Guardando…' : renewalMode ? 'Renovar QT' : editMode ? 'Update Record' : 'Save Record'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
